import type { ReportDB } from '@creator/sdk/modules/report/report.model';
import Chip from '@creator/ui/components/Chip/Chip';
import Spinner from '@creator/ui/components/Spinner/Spinner';
import Text from '@creator/ui/components/Text/Text';
import UserDisplayName from '@creator/ui/components/UserDisplayName/UserDisplayName';
import { capitalizeFirstLetter } from '@creator/ui/utils/string';
import { getQueryParam } from '@creator/ui/utils/url-utils';
import Icon from '@src/basic-components/Icon/Icon';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import { getMyUser, isUserModerator } from '@src/model/user/helpers';
import { usePaginatedReports, useReportActions, useReportsByIds } from '@src/store/reports';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import LoadMoreButton from './components/LoadMoreButton';
import ReportTable from './components/ReportTable';

export interface ReportTabProps {
    enableMultiline?: boolean;
    tokenName: string;
}

const PAGE_SIZE = 20;

const LOG_TYPES = ['all', 'post', 'comment', 'user'];
const STATUS_TYPES = ['all', 'new', 'rejected', 'done'];

type logType = (typeof LOG_TYPES)[number];

type statusType = (typeof STATUS_TYPES)[number];

const ReportTab: FC<ReportTabProps> = props => {
    const { tokenName } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();

    // New Jotai store hooks
    const { decryptReport, updateReportStatus } = useReportActions();

    // State for filters and UI
    const [selectedType, setSelectedType] = useState(LOG_TYPES[0] as logType);
    const [selectedStatus, setSelectedStatus] = useState(STATUS_TYPES[0] as logType);
    const [updateReportId, setUpdateReportId] = useState('');
    const [selectedUser, setSelectedUsers] = useState('');
    const [selectedPost, setselectedPostId] = useState({ id: '', title: '' });
    const [selectedComment, setselectedComment] = useState({ id: '', title: '' });
    const [searchParams, setSearchParams] = useState({ username: '', postId: '', type: '', commentId: '', status: '' });
    const [updatingReportType, setUpdatingReportType] = useState('');
    const [revealedReports, setRevealtedReports] = useState([] as string[]);

    // Memoized pagination params
    const paginationParams = useMemo(
        () => ({
            tokenName,
            status: selectedStatus === 'all' ? undefined : capitalizeFirstLetter(selectedStatus),
            type: selectedType === 'all' ? undefined : capitalizeFirstLetter(selectedType),
            fetchLimit: PAGE_SIZE,
        }),
        [tokenName, selectedStatus, selectedType]
    );

    // Use the new paginated reports hook
    const { reportIds, loadMore, hasMore, loadStatus, error } = usePaginatedReports(paginationParams);

    const myUser = getMyUser();
    const isModerator = myUser ? isUserModerator(myUser.id, tokenName) : false;

    // Get actual report objects from IDs
    const allReports = useReportsByIds(reportIds);

    // Apply client-side filtering for user, post, and comment filters
    const filteredReportIds = useMemo(() => {
        let filteredIds = reportIds;

        if (selectedUser && selectedUser !== '') {
            filteredIds = reportIds.filter(id => {
                const report = allReports.find(r => r?.id === id);
                return report && (report.reporterName === selectedUser || report.userName === selectedUser);
            });
        }

        if (selectedPost.id && selectedPost.id !== '') {
            filteredIds = filteredIds.filter(id => {
                const report = allReports.find(r => r?.id === id);
                return report && report.postId === selectedPost.id;
            });
        }

        if (selectedComment.id && selectedComment.id !== '') {
            filteredIds = filteredIds.filter(id => {
                const report = allReports.find(r => r?.id === id);
                return report && report.commentId === selectedComment.id;
            });
        }

        return filteredIds;
    }, [reportIds, allReports, selectedUser, selectedPost.id, selectedComment.id]);

    useEffect(() => {
        // Clear revealed reports when filters change
        setRevealtedReports([]);
    }, [selectedType, selectedUser, selectedPost, selectedStatus]);

    useEffect(() => {
        const search = window.location.search;
        if (!search) {
            setSelectedType(LOG_TYPES[0] as logType);
            setSelectedStatus(STATUS_TYPES[0] as logType);
            setselectedPostId({ id: '', title: '' });
            setselectedComment({ id: '', title: '' });
            setSelectedUsers('');
            return;
        }

        setSearchParams({
            type: getQueryParam('type') || '',
            username: getQueryParam('user') || '',
            postId: getQueryParam('post_id') || '',
            commentId: getQueryParam('comment_id') || '',
            status: getQueryParam('status') || '',
        });
    }, [window.location.href]);

    useEffect(() => {
        if (searchParams.type && searchParams.type !== 'all') setSelectedType(searchParams.type);
        if (searchParams.postId) setselectedPostId({ title: '', id: searchParams.postId });
        if (searchParams.username) setSelectedUsers(searchParams.username);
        if (searchParams.commentId) setselectedComment({ title: '', id: searchParams.commentId });
        if (searchParams.status && searchParams.status !== 'all') setSelectedStatus(searchParams.status);
    }, [searchParams]);

    useEffect(() => {
        if (!window.location.search) {
            // Reset to defaults if no search params
            setSelectedType(LOG_TYPES[0] as logType);
            setSelectedStatus(STATUS_TYPES[0] as statusType);
        }
    }, []);

    // Initial load effect - trigger loading when component mounts or filters change
    useEffect(() => {
        if (reportIds.length > 0) return; // skip load if there are items already
        if (loadStatus === 'loading') return; // avoid duplicate loading
        loadMore();
    }, [paginationParams.tokenName, paginationParams.status, paginationParams.type]);

    const onLoadMoreClick = useCallback(() => {
        loadMore();
    }, [loadMore]);

    function onStatusChange(status: string): void {
        if (status === selectedStatus) return;
        setSelectedStatus(status as statusType);
        setSelectedType('all');
        setSelectedUsers('');
        setselectedComment({ id: '', title: '' });
        setselectedPostId({ id: '', title: '' });
        const filter = '';
        onUrlFilterChange(filter, 'all', status);
    }

    function onTypeChange(type: string): void {
        if (type === selectedType) return;
        setSelectedType(type as logType);
        setSelectedStatus('all');
        setSelectedUsers('');
        setselectedComment({ id: '', title: '' });
        setselectedPostId({ id: '', title: '' });
        const filter = '';
        onUrlFilterChange(filter, type);
    }

    function onUrlFilterChange(filter: string, type?: string, status?: string): void {
        const typeUrl = type || '';
        const statusUrl = status || '';
        navigate(`${window.location.pathname}?tab=report&type=${typeUrl}&status=${statusUrl}${filter}`);
    }

    function onSelectUser(username: string): void {
        setselectedPostId({ id: '', title: '' });
        setselectedComment({ id: '', title: '' });

        setSelectedStatus('all');
        setSelectedType('all');

        if (selectedUser === username) {
            setSelectedUsers('');
            navigate(`${window.location.pathname}?tab=report`);
        } else {
            setSelectedUsers(username);
            onUrlFilterChange(`&user=${username}`, '');
        }
    }

    function onSelectPostId(postId: string, title: string, commentId?: string): void {
        setSelectedUsers('');
        setSelectedType('all');
        setSelectedStatus('all');

        if (selectedPost.id === postId && !commentId) {
            setselectedPostId({ title: '', id: '' });
            navigate(`${window.location.pathname}?tab=report`);
            return;
        }
        if (selectedComment.id === commentId && !postId) {
            setselectedComment({ id: '', title: '' });
            navigate(`${window.location.pathname}?tab=report`);
            return;
        }
        if (commentId) {
            setselectedComment({ id: commentId, title: title });
            onUrlFilterChange(`&comment_id=${commentId}`);
            return;
        }
        setselectedPostId({ id: postId, title: title });
        onUrlFilterChange(`&post_id=${postId}`);
        return;
    }

    async function onModeratorRevealUserClick(encryptedUser: string, reportId: string, tokenName: string): Promise<void> {
        if (!isModerator || !myUser) return;
        try {
            const res = await decryptReport({ reportId: reportId, tokenName, userId: myUser.id });
            setRevealtedReports([...revealedReports, reportId]);
        } catch (e) {}
    }

    function renderFilterChip({ condition, label, onRemove }: { condition: boolean; label: React.ReactNode; onRemove: () => void }) {
        if (!condition) return null;

        return (
            <Chip size="md" variant="filled" className={'cursor-pointer transition-colors duration-200'} onClick={onRemove}>
                <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">{label}</div>
                    <Icon name="close" className="flex-shrink-0 w-4 h-4 transition-opacity duration-150 opacity-70 hover:opacity-100" />
                </div>
            </Chip>
        );
    }

    function renderProfileFilter() {
        return renderFilterChip({
            condition: !!selectedUser,
            label: (
                <div className="flex items-center min-w-0 gap-2">
                    <ProfileImage tokenName={tokenName} displayName={selectedUser} className="flex-shrink-0 w-4 h-4" />
                    <UserDisplayName displayName={selectedUser} textProps={{ className: 'font-medium text-sm truncate' }} />
                </div>
            ),
            onRemove: () => onSelectUser(selectedUser),
        });
    }

    function renderPostFilter() {
        return renderFilterChip({
            condition: !!selectedPost.id,
            icon: 'post',
            label: <Text className="text-sm font-medium truncate">{selectedPost.title || <Spinner className="w-4 h-4" />}</Text>,
            onRemove: () => onSelectPostId(selectedPost.id, selectedPost.title),
            colorClasses: 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800',
            hoverClasses: 'hover:bg-green-100 dark:hover:bg-green-900',
        });
    }

    function renderCommentFilter() {
        return renderFilterChip({
            condition: !!selectedComment.id,
            icon: 'chat',
            label: <Text className="text-sm font-medium truncate">{selectedComment.title || <Spinner className="w-4 h-4" />}</Text>,
            onRemove: () => onSelectPostId('', selectedComment.title, selectedComment.id),
            colorClasses: 'bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800',
            hoverClasses: 'hover:bg-purple-100 dark:hover:bg-purple-900',
        });
    }

    function renderFilters() {
        const hasFilters = selectedUser || selectedPost.id || selectedComment.id;

        if (!hasFilters) return null;

        return (
            <div className="mb-2 space-y-2">
                <Text className="block text-sm font-semibold text-gray-700 dark:text-gray-300">{t('tokenPage.reports.activeFilters')}</Text>
                <div className="flex flex-wrap items-center gap-2">
                    {renderProfileFilter()}
                    {renderPostFilter()}
                    {renderCommentFilter()}
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (selectedPost.id && !selectedPost.title) {
            const postTitle = allReports.find(r => r.postId === selectedPost.id)?.postTitle || '';
            if (postTitle) {
                setselectedPostId({ id: selectedPost.id, title: postTitle });
            }
        }
    }, [selectedPost, allReports]);

    useEffect(() => {
        if (selectedComment.id && !selectedComment.title) {
            const commentReport = allReports.find(r => r.commentId === selectedComment.id);
            const postTitle = commentReport?.postTitle || '';
            if (postTitle) setselectedComment({ id: selectedComment.id, title: postTitle });
        }
    }, [selectedComment, allReports]);

    return (
        <div className="w-full p-4 mb-6">
            <div className="mb-4">
                <Text className="block mb-1 text-lg font-semibold">{t('tokenPage.reports.title')}</Text>
                <Text enableMultiline={true} className="text-sm text-gray-500 dark:text-gray-400">
                    {t('tokenPage.reports.subtitle')}
                </Text>
            </div>
            {renderFilters()}
            <div className="overflow-auto">
                <ReportTable
                    reportIds={filteredReportIds}
                    tokenName={tokenName}
                    loadStatus={loadStatus}
                    selectedUser={selectedUser}
                    revealedReports={revealedReports}
                    onSelectUser={onSelectUser}
                    onSelectPostId={onSelectPostId}
                    onModeratorRevealUserClick={onModeratorRevealUserClick}
                    selectedType={selectedType}
                    selectedStatus={selectedStatus}
                    onStatusChange={onStatusChange}
                    onTypeChange={onTypeChange}
                />
            </div>
            <LoadMoreButton hasMore={hasMore} loadStatus={loadStatus} onLoadMore={onLoadMoreClick} />
        </div>
    );
};

export default ReportTab;
